import React, { FunctionComponent, useState, useEffect } from 'react';
import { Header, Image, Table } from 'semantic-ui-react';
import withStyles from 'react-jss';
import { Redirect, match, withRouter } from 'react-router-dom';
import { compose } from 'recompose';
import classnames from 'classnames';
import {
  map,
  prop,
  path,
  pathOr,
  sort,
  ascend,
  addIndex,
  isEmpty,
  isNil,
} from 'ramda';

import { useUserMgmt } from 'components/UserMgmt/UserMgmt.context';
import { ClassProp } from 'helpers/types';
import visaLogo from 'assets/icons/visa.svg';
import mastercardLogo from 'assets/icons/mastercard.png';
import amexLogo from 'assets/icons/amex.svg';

import styles from './VenueTable.styles';

enum VenueStatus {
  PENDING = 'pending',
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

export type Venue = {
  locationId: string;
  offerNetworkVenueId: string;
  offerNetworkExternalVenueId: string;
  merchantName: string;
  imageUrl: string;
  address1: string;
  city: string;
  state: string;
  postalCode: number;
  cardNetworkStatus: {
    VISA: string;
    MASTERCARD: string;
    AMEX: string;
  };
  status: VenueStatus;
};

type Direction = 'ascending' | 'descending';

type NullableString = string | null;

type Styles = ClassProp<typeof styles>;

type TableLayout = {
  label: string;
  source: any;
  sortable: boolean;
};

type Props = {
  data: Venue[];
  classes: Styles;
  match: match;
  className?: string;
};

type ColumnSort = {
  column: NullableString;
  direction: Direction;
};

type RowClicked = {
  showDetails: boolean;
  offerNetworkVenueId: NullableString;
  merchantId: NullableString;
};

type CardNetworkStatus = {
  AMEX: string;
  MASTERCARD: string;
  VISA: string;
};

export const CardNetworkVenueStatus = {
  VENUE_INACTIVE: 'VENUE_INACTIVE',
  NEEDS_RESOLVE: 'NEEDS_RESOLVE',
  NOT_CONNECTED: 'NOT_CONNECTED',
  PENDING_CONNECTION: 'PENDING_CONNECTION',
  PENDING_ACTIVATION: 'PENDING_ACTIVATION',
  AWAITING_TRANSACTIONS: 'AWAITING_TRANSACTIONS',
  ACTIVATED_AND_TRANSACTING: 'ACTIVATED_AND_TRANSACTING',
};

const mapIndexed = addIndex(map);

const sorterAsce = (venues: Venue[], fieldName: string) =>
  sort(ascend(prop(fieldName)), venues as Venue[]);

const EXTERNAL_VENUE_TABLE_LAYOUT: TableLayout[] = [
  {
    label: 'Address',
    source: ['address1'],
    sortable: true,
  },
  {
    label: 'City',
    source: ['city'],
    sortable: true,
  },
  {
    label: 'State',
    source: ['state'],
    sortable: true,
  },
  {
    label: 'Zip Code',
    source: ['postalCode'],
    sortable: true,
  },
  {
    label: 'Venue Type',
    source: ['venueType'],
    sortable: true,
  },
];

const INTERNAL_VENUE_TABLE_LAYOUT: TableLayout[] = [
  {
    label: 'Store Id',
    source: ['locationId'],
    sortable: true,
  },
  ...EXTERNAL_VENUE_TABLE_LAYOUT,
  {
    label: 'Card Networks',
    source: ['cardNetworkStatus'],
    sortable: false,
  },
  {
    label: 'Transactions',
    source: ['totalTransactions'],
    sortable: true,
  },
  {
    label: 'Status',
    source: ['status'],
    sortable: true,
  },
];

const getVenueTableLayout = (isExternalUser: boolean): TableLayout[] =>
  isExternalUser ? EXTERNAL_VENUE_TABLE_LAYOUT : INTERNAL_VENUE_TABLE_LAYOUT;

const isNotValid = (data: Venue[]) => isEmpty(data) || isNil(data);

const VenueTable: FunctionComponent<Props> = ({
  data,
  match,
  classes,
  className,
}) => {
  const { isExternalUser } = useUserMgmt();
  const [columnSort, setColumnSort] = useState<ColumnSort>({
    column: null,
    direction: 'ascending',
  });

  const [rowClicked, setRowClicked] = useState<RowClicked>({
    showDetails: false,
    offerNetworkVenueId: null,
    merchantId: null,
  });

  const [venues, setVenues] = useState<Venue[]>(data);

  useEffect(() => {
    if (venues.length !== data.length) {
      setVenues(data);
    }
  });

  const handleSort = (clickedColumn: string) => () => {
    let sortedVenues: any = [];
    if (columnSort.column !== clickedColumn) {
      sortedVenues = sorterAsce(venues, clickedColumn);
      setColumnSort({ column: clickedColumn, direction: 'ascending' });
    } else {
      setColumnSort({
        column: clickedColumn,
        direction:
          columnSort.direction === 'ascending' ? 'descending' : 'ascending',
      });
      sortedVenues = venues.reverse();
    }
    setVenues(sortedVenues);
  };

  const handleRowClick = (event: any) => {
    if (isExternalUser) {
      return null;
    }

    const offerNetworkVenueId =
      path(
        ['event', 'currentTarget', 'dataset', 'offernetworkvenueid'],
        event,
      ) || event.currentTarget.getAttribute('data-offernetworkvenueid');
    const merchantId =
      path(['event', 'currentTarget', 'dataset', 'merchantid'], event) ||
      event.currentTarget.getAttribute('data-merchantid');

    setRowClicked({
      merchantId,
      offerNetworkVenueId,
      showDetails: true,
    });
  };

  const renderMerchantDetails = (venue: Venue, classes: Styles) => {
    if (!venue.imageUrl && !venue.merchantName) return null;

    return (
      <Header as="h3" className={classes.header}>
        {venue.imageUrl && (
          <Image circular src={venue.imageUrl} className={classes.logo} />
        )}
        {venue.merchantName}
      </Header>
    );
  };

  const renderTableHeader = (columnSort: ColumnSort, classes: Styles) => (
    <Table.Header>
      <Table.Row>
        {mapIndexed(
          renderHeaderCell(columnSort, classes),
          getVenueTableLayout(isExternalUser),
        )}
      </Table.Row>
    </Table.Header>
  );

  const renderHeaderCell = (columnSort: ColumnSort, classes: Styles) => (
    { label, sortable, source }: any,
    idx: number,
  ) => {
    if (sortable) {
      return (
        <Table.HeaderCell
          className={classes.tableHeader}
          sorted={
            columnSort.column === source ? columnSort.direction : undefined
          }
          onClick={handleSort(source)}
          key={`${source + idx}`}
        >
          {label}
        </Table.HeaderCell>
      );
    }

    return (
      <Table.HeaderCell className={classes.tableHeader} key={`${source + idx}`}>
        {label}
      </Table.HeaderCell>
    );
  };

  const greenCNStatus = [CardNetworkVenueStatus.ACTIVATED_AND_TRANSACTING];
  const grayCNStatus = [
    CardNetworkVenueStatus.VENUE_INACTIVE,
    CardNetworkVenueStatus.PENDING_CONNECTION,
    CardNetworkVenueStatus.PENDING_ACTIVATION,
    CardNetworkVenueStatus.AWAITING_TRANSACTIONS,
  ];
  const redCNStatus = [
    CardNetworkVenueStatus.NEEDS_RESOLVE,
    CardNetworkVenueStatus.NOT_CONNECTED,
  ];

  const divClassName = (status: string, classes: Styles) =>
    classnames(classes.tableCard, {
      [classes.tableCardRed]: redCNStatus.includes(status),
      [classes.tableCardGray]: grayCNStatus.includes(status),
    });
  const imgClassName = (status: string, classes: Styles) =>
    classnames(classes.tableCardIcon, {
      [classes.grayCardIcon]: !greenCNStatus.includes(status),
    });

  const renderCardNetworks = (
    cardNetworkStatus: CardNetworkStatus,
    classes: Styles,
  ) => (
    <>
      <div className={divClassName(cardNetworkStatus.VISA, classes)}>
        <Image
          src={visaLogo}
          className={imgClassName(cardNetworkStatus.VISA, classes)}
        />
      </div>
      <div className={divClassName(cardNetworkStatus.MASTERCARD, classes)}>
        <Image
          src={mastercardLogo}
          className={imgClassName(cardNetworkStatus.MASTERCARD, classes)}
        />
      </div>
      <div className={divClassName(cardNetworkStatus.AMEX, classes)}>
        <Image
          src={amexLogo}
          className={imgClassName(cardNetworkStatus.AMEX, classes)}
        />
      </div>
    </>
  );

  // defines custom rendering for table cells
  const customBodyCellRender: Record<string, any> = {
    cardNetworkStatus: renderCardNetworks,
  };

  const renderBodyCell = (record: Record<string, unknown>, classes: Styles) => (
    { source }: any,
    idx: number,
  ) => {
    let fieldValue = pathOr('', source, record);

    if (Object.keys(customBodyCellRender).includes(source[0])) {
      fieldValue = customBodyCellRender[source[0]](fieldValue, classes);
    } else if (Array.isArray(fieldValue)) {
      fieldValue = fieldValue.join(', ');
    }

    return <Table.Cell key={`${source[0] + idx}`}>{fieldValue}</Table.Cell>;
  };

  const renderTableRow = (classes: Styles) => (venue: Venue) => (
    <Table.Row
      key={venue.offerNetworkVenueId}
      data-offernetworkvenueid={venue.offerNetworkVenueId}
      data-merchantid={venue.offerNetworkExternalVenueId}
      onClick={handleRowClick}
      className={classnames({ [classes.selectableRow]: !isExternalUser })}
    >
      {mapIndexed(
        renderBodyCell(venue, classes),
        getVenueTableLayout(isExternalUser),
      )}
    </Table.Row>
  );

  if (rowClicked.showDetails) {
    let path = `${match.url}/venue/${rowClicked.offerNetworkVenueId}`;
    if (!match.path.includes('merchantId')) {
      path = `${path}/${rowClicked.merchantId}`;
    }
    return <Redirect to={`${path}`} push />;
  }

  if (isNotValid(venues)) {
    return (
      <Header as="h3" className={classes.header} content="No Venues found!" />
    );
  }

  return (
    <>
      {renderMerchantDetails(venues[0], classes)}

      <Table
        className={classnames(className, classes.container)}
        selectable
        sortable
      >
        {renderTableHeader(columnSort, classes)}
        <Table.Body>{map(renderTableRow(classes), venues)}</Table.Body>
      </Table>
    </>
  );
};

export default compose<Props, any>(withRouter, withStyles(styles))(VenueTable);
